<template>
    <div class="admin-album-edit">
        <div class="page-header">
            <el-page-header @back="goBack" :content="album.title || '加载中...'" />
            <div class="header-actions">
                <el-button type="primary" @click="handleAddFolder(null)">
                    <el-icon>
                        <FolderAdd />
                    </el-icon> 新建章/节
                </el-button>
            </div>
        </div>

        <div class="editor-container">
            <div class="tree-panel">
                <el-tree :data="treeData" node-key="id" default-expand-all :expand-on-click-node="false" draggable
                    @node-drop="handleDrop">
                    <template #default="{ node, data }">
                        <div class="custom-tree-node">
                            <div class="node-label">
                                <el-icon v-if="data.type === 'folder'" class="node-icon folder-icon">
                                    <Folder />
                                </el-icon>
                                <el-icon v-else class="node-icon paper-icon">
                                    <Document />
                                </el-icon>
                                <span>{{ node.label }}</span>
                                <span v-if="data.paperName" class="paper-meta">({{ data.paperName }})</span>
                            </div>
                            <div class="node-actions">
                                <el-button v-if="data.type === 'folder'" link type="primary" size="small"
                                    @click.stop="handleAddFolder(data)">
                                    + 子章节
                                </el-button>
                                <el-button v-if="data.type === 'folder'" link type="success" size="small"
                                    @click.stop="handleAddPaper(data)">
                                    + 试卷
                                </el-button>
                                <el-button link type="warning" size="small" @click.stop="handleEdit(data)">
                                    编辑
                                </el-button>
                                <el-popconfirm title="确定删除吗？" @confirm="handleDelete(data)">
                                    <template #reference>
                                        <el-button link type="danger" size="small" @click.stop>删除</el-button>
                                    </template>
                                </el-popconfirm>
                            </div>
                        </div>
                    </template>
                </el-tree>
            </div>
        </div>

        <!-- Dialogs -->
        <el-dialog v-model="folderDialogVisible" :title="isEdit ? '编辑节点' : '新建章节'" width="400px">
            <el-form :model="folderForm" label-width="80px">
                <el-form-item label="名称">
                    <el-input v-model="folderForm.name" placeholder="请输入名称" />
                </el-form-item>
            </el-form>
            <template #footer>
                <el-button @click="folderDialogVisible = false">取消</el-button>
                <el-button type="primary" @click="submitFolderForm">确定</el-button>
            </template>
        </el-dialog>

        <PaperSelectDialog v-model="paperDialogVisible" @select="onPaperSelected" />
    </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { FolderAdd, Folder, Document } from '@element-plus/icons-vue'
import { getAlbumDetail, addAlbumNode, updateAlbumNode, deleteAlbumNode } from '@/utils/api'
import { ElMessage } from 'element-plus'
import PaperSelectDialog from '@/components/PaperSelectDialog.vue'

const route = useRoute()
const router = useRouter()
const albumId = route.params.id
const album = ref({})
const treeData = ref([])
const loading = ref(false)

// Dialog states
const folderDialogVisible = ref(false)
const paperDialogVisible = ref(false)
const isEdit = ref(false)
const folderForm = ref({ id: null, name: '', parentId: null })
const currentParent = ref(null) // For adding nodes

onMounted(() => {
    fetchData()
})

const fetchData = async () => {
    loading.value = true
    try {
        const res = await getAlbumDetail(albumId)
        album.value = res.data.album
        treeData.value = res.data.tree || []
    } catch (error) {
        ElMessage.error('加载失败')
    } finally {
        loading.value = false
    }
}

const goBack = () => {
    router.push('/admin/albums')
}

// -- Folder / Node Operations --

const handleAddFolder = (parent) => {
    isEdit.value = false
    currentParent.value = parent
    folderForm.value = { name: '', parentId: parent ? parent.id : null }
    folderDialogVisible.value = true
}

const handleEdit = (data) => {
    isEdit.value = true
    folderForm.value = { id: data.id, name: data.label, parentId: data.parentId }
    folderDialogVisible.value = true
}

const submitFolderForm = async () => {
    if (!folderForm.value.name) return ElMessage.warning('请输入名称')

    try {
        if (isEdit.value) {
            await updateAlbumNode(folderForm.value.id, { name: folderForm.value.name })
            ElMessage.success('更新成功')
        } else {
            await addAlbumNode(albumId, {
                name: folderForm.value.name,
                parentId: currentParent.value ? currentParent.value.id : null,
                type: 'folder'
            })
            ElMessage.success('创建成功')
        }
        folderDialogVisible.value = false
        fetchData()
    } catch (error) {
        ElMessage.error('操作失败')
    }
}

const handleDelete = async (data) => {
    try {
        await deleteAlbumNode(data.id)
        ElMessage.success('删除成功')
        fetchData()
    } catch (error) {
        ElMessage.error('删除失败')
    }
}

// -- Paper Operations --

const handleAddPaper = (parent) => {
    currentParent.value = parent
    paperDialogVisible.value = true
}

const onPaperSelected = async (paper) => {
    try {
        await addAlbumNode(albumId, {
            name: paper.name,
            parentId: currentParent.value ? currentParent.value.id : null,
            type: 'paper',
            paperId: paper.id
        })
        ElMessage.success('添加试卷成功')
        fetchData()
    } catch (error) {
        ElMessage.error('添加失败')
    }
}

const handleDrop = async (draggingNode, dropNode, dropType, ev) => {
    // Basic drag support: implement updateAlbumNode call to change parent/order
    // Note: This is complex because dropType can be 'before', 'after', 'inner'
    // For MVP, just reloading or implementing basic parent change
    const draggedData = draggingNode.data
    const dropData = dropNode.data

    let newParentId = null
    if (dropType === 'inner') {
        newParentId = dropData.id
    } else {
        newParentId = dropData.parentId
    }

    try {
        await updateAlbumNode(draggedData.id, {
            name: draggedData.label,
            parentId: newParentId
        })
        ElMessage.success('移动成功')
    } catch (err) {
        ElMessage.error('移动失败')
        fetchData() // Revert
    }
}

</script>

<style scoped>
.admin-album-edit {
    padding: 20px;
    background: #fff;
    min-height: calc(100vh - 100px);
}

.page-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-bottom: 1px solid #eee;
    padding-bottom: 20px;
    margin-bottom: 20px;
}

.editor-container {
    max-width: 1000px;
}

.custom-tree-node {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-size: 14px;
    padding-right: 8px;
}

.node-label {
    display: flex;
    align-items: center;
    gap: 5px;
}

.node-icon {
    font-size: 16px;
}

.folder-icon {
    color: #409EFF;
}

.paper-icon {
    color: #67C23A;
}

.paper-meta {
    color: #999;
    font-size: 12px;
    margin-left: 5px;
}

.node-actions {
    opacity: 0;
    transition: opacity 0.2s;
}

.custom-tree-node:hover .node-actions {
    opacity: 1;
}

/* Element Plus Tree overrides for better spacing */
:deep(.el-tree-node__content) {
    height: 40px;
}
</style>
